import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { AddEditCategoryModal } from './ActionModals/AddEditCategoryModal';
import { DeleteConfirmationModal } from './ActionModals/DeleteConfirmationModal';
import { CategoryTable } from './Components/CategoryTable';
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from './Helpers/Api';

const InventoryCategoryManagement = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await getAllCategories();
      setCategories(data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const validationSchema = Yup.object({
    name: Yup.string().required('Category name is required'),
  });

  const formik = useFormik({
    initialValues: { name: '' },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        if (modal === 'add') {
          await createCategory({ name: values.name });
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Category saved successfully!',
          });
        } else if (modal === 'edit') {
          await updateCategory(selectedCategory._id, values);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Category updated successfully!',
          });
        }

        await fetchCategories();
        closeModal();
        resetForm();
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = useCallback(() => {
    setSelectedCategory(null);
    setModal('add');
    formik.setValues({ name: '' });
  }, [formik]);

  const openEditModal = useCallback((category) => {
    setSelectedCategory(category);
    setModal('edit');
    formik.setValues({ name: category.name || '' });
  }, [formik]);

  const openDeleteModal = useCallback((category) => {
    setSelectedCategory(category);
    setModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    setModal('');
    setSelectedCategory(null);
    formik.resetForm();
  }, [formik]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteCategory(selectedCategory._id);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Category deleted successfully!',
      });
      await fetchCategories();
      closeModal();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, fetchCategories, selectedCategory, closeModal]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Category Management</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Inventory" breadcrumbItem="Item Categories" />

        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" onClick={openAddModal}>
            Add Category
          </button>
        </div>

        <CategoryTable
          items={categories}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />


        <AddEditCategoryModal
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          onClose={closeModal}
        />

        <DeleteConfirmationModal
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={selectedCategory}
        />
      </div>
    </div>
  );
};

export { InventoryCategoryManagement };